import { useRef, useState } from 'react';
import { RadioGroup, Stack, ValidationMessage } from '@etg/wings';
import { useTranslation, useValidation } from '@eti/providers';
import { constants, usePrevious } from '@eti/utils';
import { css } from '@eti/styles';
import { Field, FormSection } from 'redux-form';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.

import { screenReaderOnly } from '../../../common/utils/styles';
import useBaggageTracker from '../hooks/useBaggageTracker';
import { usePersuasionModal } from '../../../common/context/PersuasionModalContext';
import { isEnabled } from '../../../common/utils/modal';
import ProductModal from '../../../common/components/ProductModal';
import { BAGGAGE_VALUES } from '../utils/baggageUtils';
import BaggageSelectionRadioBoxLabel from './BaggageSelectionRadioBoxLabel';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const errorStyles = css`
  margin-inline-start: 0;
`;
const BaggageSelectionRadioGroup = ({
  baggageOptions,
  firstTravelerSelectionValue,
  input,
  meta,
  onChange,
  order,
  persuasionContent,
  persuasionTitle,
  productName,
  productPosition,
  radioGroupScreenReaderDescription,
  rootElement,
  shouldApplySelectionToAll,
  travelerId,
  totalBaggageQuantity,
  secondBagProductId
}) => {
  var _usePrevious;
  const {
    t
  } = useTranslation();
  const [selectedBaggageOption, setSelectedBaggageOption] = useState('');
  const previousSelectedBaggageOption = (_usePrevious = usePrevious(firstTravelerSelectionValue)) !== null && _usePrevious !== void 0 ? _usePrevious : '';
  const yesOptionRef = useRef(null);
  const setYesOptionRef = el => {
    yesOptionRef.current = el;
  };
  const {
    isOpen,
    onConfirm,
    onDecline,
    setIsOpen,
    setOnConfirm,
    setOnDecline
  } = usePersuasionModal();
  const hasError = Boolean((meta === null || meta === void 0 ? void 0 : meta.touched) && (meta === null || meta === void 0 ? void 0 : meta.error));
  const selectedTraveler = order.travelers[Number(travelerId)];
  const travelersWithoutInfants = order.travelers.filter(({
    ageType
  }) => ageType !== constants.ageTypes.INFANT);
  const {
    addToCartEventOnChange,
    addToCartModalEvent
  } = useBaggageTracker(productName, travelersWithoutInfants.length, totalBaggageQuantity, travelerId, productPosition, shouldApplySelectionToAll, rootElement);
  const applySelection = (value, travelerId) => {
    onChange(value, travelerId, shouldApplySelectionToAll);
    setSelectedBaggageOption(value);
  };
  const handleChange = (selectedTravelerId, selectedValue) => {
    var _order$persuasion;
    if (!selectedValue) {
      return;
    }
    const isFirstTraveler = Number(selectedTravelerId) === 0;
    const shouldShowPersuasionModal = isFirstTraveler && (order === null || order === void 0 || (_order$persuasion = order.persuasion) === null || _order$persuasion === void 0 ? void 0 : _order$persuasion.isEnabled) && selectedValue === BAGGAGE_VALUES.FALSE;
    applySelection(selectedValue, selectedTravelerId);
    addToCartEventOnChange(selectedValue);
    if (!shouldShowPersuasionModal) {
      return;
    }
    setIsOpen(true);
    setOnConfirm(() => () => {
      var _baggageOptions$;
      const defaultFallbackValue = ((_baggageOptions$ = baggageOptions[1]) === null || _baggageOptions$ === void 0 ? void 0 : _baggageOptions$.value) || '';
      const value = selectedBaggageOption || previousSelectedBaggageOption || defaultFallbackValue;
      setIsOpen(false);
      setTimeout(() => {
        var _yesOptionRef$current;
        applySelection(value, selectedTravelerId);
        (_yesOptionRef$current = yesOptionRef.current) === null || _yesOptionRef$current === void 0 || _yesOptionRef$current.focus();
      }, 530);
    });
    setOnDecline(() => () => {
      applySelection(BAGGAGE_VALUES.FALSE, selectedTravelerId);
      setIsOpen(false);
    });
  };
  const handleOnConfirm = () => {
    onConfirm();
    addToCartModalEvent(true);
  };
  const handleOnDecline = () => {
    onDecline();
    addToCartModalEvent(false);
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElement,
    children: [isEnabled(order === null || order === void 0 ? void 0 : order.persuasion) && /*#__PURE__*/_jsx(ProductModal, {
      content: persuasionContent,
      isOpen: isOpen,
      onConfirm: handleOnConfirm,
      onDecline: handleOnDecline,
      productName: productName,
      title: persuasionTitle
    }), /*#__PURE__*/_jsxs(Stack, {
      spacing: 8,
      children: [hasError && /*#__PURE__*/_jsx(ValidationMessage, {
        className: errorStyles,
        "data-testid": "purchasableBaggage-select-option-error-message",
        variant: "error",
        children: t('Product.SelectOneValidation.Message')
      }), /*#__PURE__*/_jsxs(RadioGroup, {
        "data-testid": `${productName}-radiogroup-${travelerId}`,
        hasError: hasError,
        name: `${productName}-traveler-${travelerId}`,
        onChange: event => handleChange(String(selectedTraveler.id), event === null || event === void 0 ? void 0 : event.target.value),
        selectedValue: input === null || input === void 0 ? void 0 : input.value,
        children: [radioGroupScreenReaderDescription && /*#__PURE__*/_jsx(RadioGroup.Legend, {
          className: screenReaderOnly,
          children: hasError ? t('Product.SelectOneValidation.Message') : radioGroupScreenReaderDescription
        }), /*#__PURE__*/_jsx(Stack, {
          "data-offer-rate": order.id,
          spacing: 8,
          children: baggageOptions === null || baggageOptions === void 0 ? void 0 : baggageOptions.map(option => /*#__PURE__*/_jsx(BaggageSelectionRadioBoxLabel, {
            baggageOption: option,
            input: input,
            order: order,
            productName: productName,
            secondBagProductId: secondBagProductId,
            setYesOptionRef: setYesOptionRef
          }, `${productName}-traveler-${travelerId}-${option.id}`))
        })]
      })]
    })]
  });
};
const BaggageSelection = ({
  baggageOptions,
  firstTravelerSelectionValue,
  onChange,
  order,
  persuasionContent,
  persuasionTitle,
  productName,
  radioGroupScreenReaderDescription,
  rootElement,
  productPosition,
  secondBagProductId,
  shouldApplySelectionToAll,
  travelerId,
  totalBaggageQuantity
}) => {
  const validate = useValidation(productName);
  const name = `${productName}-traveler-${travelerId}`;
  return /*#__PURE__*/_jsx(FormSection, {
    name: productName,
    children: /*#__PURE__*/_jsx(Field, {
      component: BaggageSelectionRadioGroup,
      format: reduxValue => (reduxValue === null || reduxValue === void 0 ? void 0 : reduxValue.value) || undefined,
      name: name,
      props: {
        baggageOptions,
        firstTravelerSelectionValue,
        onChange,
        order,
        persuasionContent,
        persuasionTitle,
        productName,
        productPosition,
        radioGroupScreenReaderDescription,
        rootElement,
        travelerId,
        secondBagProductId,
        shouldApplySelectionToAll,
        totalBaggageQuantity
      },
      validate: validate
    })
  });
};
export default BaggageSelection;
//# sourceMappingURL=BaggageSelection.js.map